import React, {useMemo} from 'react';
import styles from './BenefactorConsentedDocumentsTable.module.scss';
import {Icon, TableColumnConfig, Text, Label} from '@gravity-ui/uikit';
import {DataTable} from '../../../../shared';
import {BenefactorConsentedDocument} from '../../../consents';
import {formatDate} from 'date-fns';
import {Archive, FileCheck} from '@gravity-ui/icons';
import classNames from 'classnames';
import {useNavigate} from 'react-router';

interface BenefactorConsentedDocumentsTableProps {
  documents: BenefactorConsentedDocument[];
  isLoading: boolean;
}

export const BenefactorConsentedDocumentsTable = ({documents, isLoading}: BenefactorConsentedDocumentsTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  // Фильтруем только принятые документы (те, у которых есть consentedAt)

  const getStatusLabel = (status: 'Approved' | 'Rejected' | null) => {
    if (status === 'Approved') {
      return (
        <Label size="xs" theme="success">
          Одобрен
        </Label>
      );
    }
    if (status === 'Rejected') {
      return (
        <Label size="xs" theme="danger">
          Отклонен
        </Label>
      );
    }
    return (
      <Label size="xs" theme="normal">
        Нет ответа
      </Label>
    );
  };

  const columns = useMemo<TableColumnConfig<BenefactorConsentedDocument>[]>(
    () => [
      {
        id: 'title',
        name: 'Название документа',
        template: (item: BenefactorConsentedDocument) => (
          <div>
            <Icon
              size={16}
              data={!item.isArchived ? FileCheck : Archive}
              className={classNames(styles.fileIcon, {[styles.archivedIcon]: item.isArchived})}
            />{' '}
            <Text variant="body-1" className={styles.titleText}>
              {item.title}
            </Text>
          </div>
        ),
      },
      {
        id: 'version',
        name: 'Последний номер версии',
        width: 200,
        template: (item: BenefactorConsentedDocument) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.latestDocumentVersionId || '-'}</Text>
          </div>
        ),
      },
      {
        id: 'lastConsentedVersion',
        name: 'Последняя принятая версия',
        width: 200,
        template: (item: BenefactorConsentedDocument) => (
          <div className={styles.cellContainer}>
            <Text variant="body-1">{item.latestConsentedDocumentVersionId || '-'}</Text>
          </div>
        ),
      },
      {
        id: 'consentedAt',
        name: 'Дата принятия',
        width: 200,
        template: (item: BenefactorConsentedDocument) => (
          <div className={styles.cellContainer}>
            {item.consentedAt ? (
              <Text variant="body-1">{formatDate(item.consentedAt, 'dd.MM.yyyy HH:mm')}</Text>
            ) : (
              <Text variant="body-1">-</Text>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        name: 'Статус',
        width: 150,
        template: (item: BenefactorConsentedDocument) => (
          <div className={styles.cellContainer}>{getStatusLabel(item.status)}</div>
        ),
      },
    ],
    [],
  );

  const handleRowClick = (document: BenefactorConsentedDocument) => {
    navigate(`/administration/consents-documents/${document.documentId}`);
  };

  return (
    <div className={styles.tableContainer}>
      <DataTable<BenefactorConsentedDocument>
        isLoading={isLoading}
        onRowClick={handleRowClick}
        data={documents}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Нет принятых документов"
        emptyStateDescription="У этого благотворителя пока нет принятых документов"
      />
    </div>
  );
};
